import {RequestMethod} from 'Shared/utilities/fetchHelpers';
import {
  A360AdvancedStatement,
  A360EarnedStatement,
  A360EmployerSummary,
  CommissionSummary,
  PaymentReceipt,
  Producer,
} from '../components/A360Commissions/types';
import {extractProducerCodes} from '../components/A360Commissions/utilities';
import {fetchA360CommissionsData} from './apiUtils';

const A360_COMMMISSIONS_ROOT_URI = 'api/commissions/v1/commissionStatements';

enum API_URL {
  COMMISSION_PAYABLE = 'commissionsPayable',
  COMMISSION_SUMMARY = 'commissionSummary',
  EMPLOYER_FILTER = 'employerFilter',
  EMPLOYER_SUMMARY = 'employerSummary',
  EARNED_STATEMENTS = 'earnedStatementSummary',
  ADVANCED_STATEMENTS = 'advancedReport',
  PRODUCER_FILTER = 'producerFilter',
  STATEMENT_ADJUSTMENT_DETAILS = 'statementAdjustmentDetails',
  STATEMENT_DATE_FILTER = 'statementDatesFilter',
  PAYMENT_RECEIPTS = 'paymentReceipts',
}

const buildA360CompensationRequestPayload = (
  agencyCodes?: string[],
  statementDates?: string[],
  employerNames?: string[],
  limit?: number,
  offset?: number,
  sortFields?: string[]
) => {
  return {
    agencyCodes: extractProducerCodes(agencyCodes),
    statementDates,
    employerNames,
    limit,
    offset,
    sortFields,
  };
};

type RequestPayload = ReturnType<typeof buildA360CompensationRequestPayload>;

const fetchDataFromAPI = fetchA360CommissionsData(A360_COMMMISSIONS_ROOT_URI);

export default {
  fetchProducerCodesFilterData: async (isNewYorkSelected: boolean) => {
    return fetchDataFromAPI<Producer[]>(
      `${API_URL.PRODUCER_FILTER}?commissionView=dataDashboard&isNewYork=${isNewYorkSelected}`,
      RequestMethod.GET
    );
  },

  fetchStatementDatesFilterData: async (agencyCodes: string[]) => {
    const requestPayload = buildA360CompensationRequestPayload(agencyCodes);
    return fetchDataFromAPI<string, RequestPayload>(API_URL.STATEMENT_DATE_FILTER, RequestMethod.POST, requestPayload);
  },

  fetchEmployerNamesFilterData: async (agencyCodes: string[], statementDates: string[]) => {
    const requestPayload = buildA360CompensationRequestPayload(agencyCodes, statementDates);
    return fetchDataFromAPI<string, RequestPayload>(API_URL.EMPLOYER_FILTER, RequestMethod.POST, requestPayload);
  },

  fetchEmployerSummary: async (
    agencyCodes: string[],
    statementDates: string[],
    employerNames: string[],
    limit: number,
    offset: number,
    sortFields: string[]
  ) => {
    const requestPayload = buildA360CompensationRequestPayload(
      agencyCodes,
      statementDates,
      employerNames,
      limit,
      offset,
      sortFields
    );
    return fetchDataFromAPI<A360EmployerSummary, RequestPayload>(
      API_URL.EMPLOYER_SUMMARY,
      RequestMethod.POST,
      requestPayload
    );
  },

  fetchEarnedStatements: async (
    agencyCodes: string[],
    statementDates: string[],
    employerNames: string[],
    limit: number,
    offset: number,
    sortFields: string[]
  ) => {
    const requestPayload = buildA360CompensationRequestPayload(
      agencyCodes,
      statementDates,
      employerNames,
      limit,
      offset,
      sortFields
    );
    return fetchDataFromAPI<A360EarnedStatement, RequestPayload>(
      API_URL.EARNED_STATEMENTS,
      RequestMethod.POST,
      requestPayload
    );
  },

  fetchAdvancedStatements: async (
    agencyCodes: string[],
    statementDates: string[],
    employerNames: string[],
    limit: number,
    offset: number,
    sortFields: string[]
  ) => {
    const requestPayload = buildA360CompensationRequestPayload(
      agencyCodes,
      statementDates,
      employerNames,
      limit,
      offset,
      sortFields.map(field => (field === 'reason' ? 'advanceReason' : field === '-reason' ? '-advanceReason' : field))
    );
    return fetchDataFromAPI<A360AdvancedStatement, RequestPayload>(
      API_URL.ADVANCED_STATEMENTS,
      RequestMethod.POST,
      requestPayload
    );
  },

  fetchCommissionPayable: async (agencyCodes: string[], statementDates: string[], employerNames: string[]) => {
    const requestPayload = buildA360CompensationRequestPayload(agencyCodes, statementDates, employerNames);
    return fetchDataFromAPI<string, RequestPayload>(API_URL.COMMISSION_PAYABLE, RequestMethod.POST, requestPayload);
  },

  fetchPaymentReceipts: async (agencyCodes: string[], statementDates: string[], employerNames: string[]) => {
    const requestPayload = buildA360CompensationRequestPayload(agencyCodes, statementDates, employerNames);
    return fetchDataFromAPI<PaymentReceipt[], RequestPayload>(
      API_URL.PAYMENT_RECEIPTS,
      RequestMethod.POST,
      requestPayload
    );
  },

  fetchAdjustmentDetails: async (agencyCodes: string[], statementDates: string[], employerNames: string[]) => {
    const requestPayload = buildA360CompensationRequestPayload(agencyCodes, statementDates, employerNames);
    return fetchDataFromAPI<A360EarnedStatement, RequestPayload>(
      API_URL.STATEMENT_ADJUSTMENT_DETAILS,
      RequestMethod.POST,
      requestPayload
    );
  },

  fetchCommissionSummary: async (agencyCodes: string[], statementDates: string[], employerNames: string[]) => {
    const requestPayload = buildA360CompensationRequestPayload(agencyCodes, statementDates, employerNames);
    return fetchDataFromAPI<CommissionSummary, RequestPayload>(
      API_URL.COMMISSION_SUMMARY,
      RequestMethod.POST,
      requestPayload
    );
  },
};
